import axios from 'axios';
import { auth } from '../config/firebase';
// Multi-tab fix (mayo 2026): el cliente activo se lee de sessionStorage
// (per-tab) en lugar de localStorage (cross-tab). Ver clientStorage.ts.
import {
  getStoredSelectedClientRaw,
  removeStoredSelectedClient,
} from './clientStorage';

// Check if we're in development or production
const isDevelopment = process.env.NODE_ENV === 'development';
// REACT_APP_API_URL toma precedencia SIEMPRE que esté seteada (dev o prod).
// - Prod normal (Firebase Hosting): NO se setea -> '' (mismo origin via rewrites).
// - Staging (nginx en otro origin): se setea a la URL absoluta de las Functions
//   de prod para reusar el backend de producción idéntico.
// - Dev sin env var: fallback a localhost:3001.
const baseURL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL
  : (isDevelopment ? 'http://localhost:3001' : '');

// PT-#75: el export largo (POST /api/export/multi-toma-stream) NO puede pasar por Firebase
// Hosting. El edge/GFE bufferea la respuesta dinámica y la corta a los ~60s (además del cap
// de 32 MiB): los heartbeats NDJSON no la mantienen viva (0 llegan al cliente) y devuelve un
// 502 de Google. La URL directa de Cloud Run del function `api` NO tiene ese cap (timeout del
// service = 540s) y streamea bien. Por eso ESTE endpoint apunta directo a Cloud Run en cualquier
// entorno desplegado; en local usa el backend local. Override explícito vía
// REACT_APP_EXPORT_STREAM_URL. Follow-up P3: mover esta URL a runtime config (Firestore/env).
const CLOUD_RUN_API_URL = 'https://api-7cudta2zjq-uc.a.run.app';
const isLocalBackend = !baseURL || /localhost|127\.0\.0\.1/.test(baseURL);
export const exportStreamBaseURL =
  process.env.REACT_APP_EXPORT_STREAM_URL ||
  (isDevelopment && isLocalBackend ? baseURL : CLOUD_RUN_API_URL);

const api = axios.create({
  baseURL,
  timeout: 120000, // Aumentado a 2 minutos para importaciones grandes
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});


// Debug drawer instrumentation: capturamos el inicio de cada request si
// el drawer está habilitado (solo superadmins). Cero overhead si no.
const debugRequestStart = (config: any) => {
  try {
    const dbg = (window as any).__debugDrawer;
    if (!dbg || !dbg.enabled()) return;
    const startMark = performance.now();
    const url = config.url || '';
    const fullUrl = (config.baseURL || '') + url;
    const id = dbg.pushNetworkStart({
      method: (config.method || 'GET').toUpperCase(),
      url,
      fullUrl,
      requestHeaders: { ...(config.headers || {}) },
      requestData: config.data,
    });
    config.__debugId = id;
    config.__debugStart = startMark;
  } catch {
    // ignore
  }
};

const debugResponseEnd = (response: any) => {
  try {
    const dbg = (window as any).__debugDrawer;
    if (!dbg) return;
    const cfg = response?.config || {};
    if (!cfg.__debugId) return;
    const durationMs = Math.round(performance.now() - (cfg.__debugStart || performance.now()));
    let sizeBytes: number | undefined;
    try {
      const cl = response.headers?.['content-length'];
      if (cl) sizeBytes = parseInt(cl, 10);
      else if (typeof response.data === 'string') sizeBytes = response.data.length;
      else if (response.data) sizeBytes = JSON.stringify(response.data).length;
    } catch {
      // ignore
    }
    dbg.pushNetworkEnd(cfg.__debugId, {
      status: response.status,
      statusText: response.statusText,
      durationMs,
      sizeBytes,
      responseHeaders: { ...(response.headers || {}) },
      responseData: response.data,
    });
  } catch {
    // ignore
  }
};

const debugResponseError = (error: any) => {
  try {
    const dbg = (window as any).__debugDrawer;
    if (!dbg) return;
    const cfg = error?.config || {};
    if (!cfg.__debugId) return;
    const durationMs = Math.round(performance.now() - (cfg.__debugStart || performance.now()));
    dbg.pushNetworkEnd(cfg.__debugId, {
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      durationMs,
      error: error?.message || String(error),
      responseHeaders: error?.response?.headers ? { ...error.response.headers } : undefined,
      responseData: error?.response?.data,
    });
  } catch {
    // ignore
  }
};

// Request interceptor function
const requestInterceptor = async (config: any) => {
  config.headers = config.headers || {};
  debugRequestStart(config);

  // Add Firebase Auth token to all requests
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }

  try {
    const selectedClient = getStoredSelectedClientRaw();

    if (selectedClient) {
      const parsedClient = JSON.parse(selectedClient);

      if (parsedClient?.clientId) {
        const headerKeys = Object.keys(config.headers || {});
        const findHeaderKey = (target: string) =>
          headerKeys.find((key) => key.toLowerCase() === target.toLowerCase());

        const clientIdHeaderKey = findHeaderKey('x-client-id');
        const clientNameHeaderKey = findHeaderKey('x-client-name');

        if (!clientIdHeaderKey) {
          config.headers['x-client-id'] = parsedClient.clientId;
        } else if (
          !config.headers[clientIdHeaderKey] ||
          config.headers[clientIdHeaderKey] === 'admin-mode'
        ) {
          config.headers[clientIdHeaderKey] = parsedClient.clientId;
        }

        if (parsedClient.clientName) {
          if (!clientNameHeaderKey) {
            config.headers['x-client-name'] = parsedClient.clientName;
          } else if (!config.headers[clientNameHeaderKey]) {
            config.headers[clientNameHeaderKey] = parsedClient.clientName;
          }
        }
      }
    }
  } catch (error) {
  }

  // Remove Content-Type header if sending FormData to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
};

// Activity tracking (GH #21 / #82): registrar acciones de escritura observadas
// por el interceptor. Fire-and-forget, non-fatal. Import perezoso para evitar
// ciclo de módulos (activityLogger importa este `api`).
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const logActionFromResponse = (response: any) => {
  try {
    const cfg = response?.config || {};
    const method = String(cfg.method || 'GET').toUpperCase();
    if (!WRITE_METHODS.has(method)) return;
    const url = String(cfg.url || '');
    // CRÍTICO: no loguear el propio POST /api/activity (loop infinito).
    if (url.includes('/api/activity')) return;
    // Import diferido: rompe el ciclo activityLogger <-> api en carga de módulo.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { activity } = require('./activityLogger');
    activity.action(`${method} ${url}`, {
      method,
      status: response?.status,
      path: url,
    });
  } catch {
    // ignore — tracking nunca rompe la respuesta
  }
};

// Response interceptor function
const responseInterceptor = (response: any) => {
  debugResponseEnd(response);
  logActionFromResponse(response);
  return response;
};

const responseErrorInterceptor = (error: any) => {
  debugResponseError(error);
  if (error.response?.status === 404 && error.response?.data?.error === 'Client not found') {
    // Check if we're in admin mode or admin route before clearing client selection
    const isAdminRoute = error.config?.url?.includes('/admin/') || error.config?.url?.includes('/api/admin/');
    
    if (!isAdminRoute) {
      try {
        const selectedClient = getStoredSelectedClientRaw();
        if (selectedClient) {
          const parsedClient = JSON.parse(selectedClient);
          // Don't clear admin-mode client
          if (parsedClient.clientId !== 'admin-mode') {
            removeStoredSelectedClient();
            window.location.reload();
          }
        }
      } catch (e) {
        // If there's an error parsing, just clear it
        removeStoredSelectedClient();
        window.location.reload();
      }
    }
  }
  return Promise.reject(error);
};

// Apply interceptors to main api instance
api.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Helper function to ensure client headers are added
export const ensureClientHeaders = (config: any = {}) => {
  const selectedClient = getStoredSelectedClientRaw();
  if (selectedClient) {
    try {
      const parsedClient = JSON.parse(selectedClient);
      // Always add client ID header, including for admin-mode
      if (parsedClient.clientId) {
        config.headers = config.headers || {};
        config.headers['x-client-id'] = parsedClient.clientId;
        if (parsedClient.clientName) {
          config.headers['x-client-name'] = parsedClient.clientName;
        }
      }
    } catch (error) {
    }
  }
  return config;
};

// Crear una instancia especial para operaciones largas como importación de Excel
export const apiLongOperation = axios.create({
  baseURL,
  timeout: 600000, // 10 minutos para operaciones largas
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});

// Aplicar los mismos interceptores a la instancia de operaciones largas
apiLongOperation.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
apiLongOperation.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export default api;
